import Vue from 'vue';
import AuthUtils from './AuthUtils';
import { SubOrganization } from '@/utils/SubOrganizationUtils';
import { v4 as uuidv4 } from 'uuid';
import SurveyUtils, {
  QuestionParticipation,
  SurveyParticipation,
  Survey,
  Section,
  Question,
  Page,
} from '@/utils/SurveyUtils';
import { LanguageObject, languageobj } from '@/i18n';
import { PointsOfService } from '@/utils/PointsOfServiceUtils';
import SurveyNodeUtils from './SurveyNodeUtils';
import { Document } from './DocumentsUtils';

type NodeType =
  | 'root'
  | 'documents'
  | 'resources'
  | 'label'
  | 'section'
  | 'page'
  | 'question';
interface ReportNode<T = any, D = ReportNodeData> {
  uuid: string;
  display: LanguageObject; // used for navigation display
  type: NodeType; // component that is rendered for this node
  item: T; // the actual item that the node is wrapping
  data: D;
  children: ReportNode[];
  parent?: ReportNode;
}

interface ReportNodeData {
  visible: boolean;
}

interface SectionData extends ReportNodeData {
  suborganization?: SubOrganization;
}

interface PageData extends ReportNodeData {
  //
}

interface QuestionData extends ReportNodeData {
  valid: boolean;
  editable: boolean;
  dirty: boolean;
  participation?: QuestionParticipation; // only when of type question
  conditional: {
    visible?: boolean;
  };
}

interface ReportContext {
  path: number[];
  nodes?: ReportNode[];
  survey?: Survey;
  surveyParticipation?: SurveyParticipation;
  questions?: Question[];
  documents: {
    items?: Document[];
    options: {
      showAllDocuments: boolean;
    };
  };
  suborganizations?: SubOrganization[];
  currentSubOrg?: SubOrganization;
  pointsofservices?: PointsOfService[];
  mode: ('preview' | 'readonly' | 'worksheet' | 'table')[];
}

const ReportVM = new Vue({
  data(): ReportContext {
    return {
      path: [],
      nodes: undefined,
      survey: undefined,
      surveyParticipation: undefined,
      questions: undefined,
      documents: {
        items: undefined,
        options: {
          showAllDocuments: false,
        },
      },
      suborganizations: undefined,
      pointsofservices: undefined,
      currentSubOrg: undefined,
      mode: [],
    };
  },

  watch: {
    '$session.role'() {
      if (!this.$session?.role) {
        this.clear();
      }
    },

    async survey() {
      // when the survey changes the node structure must be generated
      this.nodes = undefined;
      if (this.survey?.layout) {
        if (this.survey) {
          this.questions = await SurveyUtils.api.question.list({
            surveyid: this.survey?.id,
          });
          this.mergeQuestions(this.survey?.layout, this.questions);
        }

        this.nodes = SurveyNodeUtils.generateNodes(
          this.$data as ReportContext,
          this.survey?.layout
        );
      }
    },
  },

  methods: {
    clear() {
      this.path = [];
      this.nodes = undefined;
      this.survey = undefined;
      this.surveyParticipation = undefined;
      this.questions = undefined;
      this.documents = {
        items: undefined,
        options: {
          showAllDocuments: false,
        },
      };
      this.suborganizations = undefined;
      this.currentSubOrg = undefined;
      this.pointsofservices = undefined;
      this.mode = [];
    },

    mergeQuestions(layout: Partial<Section | Page>[], questions: Question[]) {
      for (const i in layout) {
        if (layout[i].type === 'page') {
          const page = layout[i] as Page;
          for (const j in page.questions) {
            Object.assign(
              page.questions[j],
              questions?.find(
                (question) => question.id === page.questions[j].id
              )
            );
          }
        } else if (layout[i].type === 'section') {
          const section = layout[i] as Section;
          this.mergeQuestions(section.children, questions);
        }
      }
    },
  },
});

const ReportStore = ReportVM.$data as ReportContext;

export {
  SectionData,
  QuestionData,
  PageData,
  ReportNode,
  NodeType,
  ReportVM,
  ReportStore,
  ReportContext,
};
